import { useEffect, useMemo } from "react";

import { useOrganizationContext } from "@/contexts/OrganizationContext";
import { usePatientOrganizationContext } from "@/contexts/PatientOrganizationContext";
// Import direto do helper: o `index` do ecommerce puxaria o módulo inteiro.
import { isEcommerceOrganization } from "src-next/modules/ecommerce/helpers/isEcommerceOrganization";
import {
  consentPatientsNotAttended,
  mergeOrganizationRecipients,
} from "src-next/modules/organization-patients";
import type { MessagingPatientAudience } from "src-next/modules/crm-clinical/messaging/types";
import { isVetApp } from "../../messaging/helpers/isVetApp";

import { mapCrmPatientSummaryToMessagingPatient } from "../helpers/mapCrmPatientSummaryToMessagingPatient";
import { consentAudienceLabel } from "../helpers/patientFinderAudience";
import {
  mapFhirPatientToCrmPatientSummary,
  mapPatientEntriesToCrmPatientSummaries,
} from "../helpers/mapFhirPatientToCrmPatientSummary";
import { mapPersonsToCrmPatientSummaries } from "../helpers/mapFhirPersonToCrmPatientSummary";
import type { CrmPatientSummary } from "../types";

function mapConsentPatients(resources: fhir5.Patient[]): CrmPatientSummary[] {
  const result: CrmPatientSummary[] = [];
  for (const resource of resources) {
    const mapped = mapFhirPatientToCrmPatientSummary(resource);
    if (mapped) result.push(mapped);
  }
  return result;
}

/**
 * Públicos da organização no formato do `SendWhatsAppMessageDialog`.
 *
 * - `enabled: false` (dialog fechado): não pede nada e devolve `undefined`,
 *   para não converter milhares de pacientes sem necessidade.
 * - Ao ligar, pede consentimento e clientes cadastrados ao contexto
 *   (carga sob demanda, com cache compartilhado).
 * - Modo vet: `undefined` — lá o destinatário é sempre o tutor.
 * - Ordem: "Todos" primeiro (já selecionado). Ecommerce sem "Pacientes atendidos".
 * - Person vira `forReference: Person/{id}` pelo conversor já existente.
 */
export function useOrganizationMessagingAudiences({
  enabled,
}: {
  enabled: boolean;
}): MessagingPatientAudience[] | undefined {
  const vetApp = isVetApp();
  // Defesa: fora do Provider (ex.: entrada `index-next`) o contexto é `null`.
  const context = usePatientOrganizationContext();
  const {
    patients,
    patientOrganizationLoading,
    consentPatients,
    consentPatientsLoading,
    requestConsentPatients,
    registeredPersons,
    registeredPersonsLoading,
    requestRegisteredPersons,
  } = context ?? ({} as Partial<NonNullable<typeof context>>);

  // Mesma defesa contra contexto `null` para a organização.
  const organizationContext = useOrganizationContext();
  const isEcommerceOrg = isEcommerceOrganization(
    organizationContext?.organization,
  );

  useEffect(() => {
    if (!enabled || vetApp) return;
    requestConsentPatients?.();
    requestRegisteredPersons?.();
  }, [enabled, vetApp, requestConsentPatients, requestRegisteredPersons]);

  return useMemo(() => {
    if (!enabled || vetApp) return undefined;

    /**
     * Ecommerce não trabalha com consultas: sem o público de atendidos,
     * nem dentro de "Todos". Também não converte essa lista (pode ter
     * milhares de pacientes) só para descartar depois.
     */
    const appointmentPatients = isEcommerceOrg
      ? []
      : mapPatientEntriesToCrmPatientSummaries(patients);
    const consent = mapConsentPatients(consentPatients ?? []);
    const registeredClients = mapPersonsToCrmPatientSummaries(
      registeredPersons ?? [],
    );
    const toMessaging = (list: CrmPatientSummary[]) =>
      list.map(mapCrmPatientSummaryToMessagingPatient);

    const extrasLoading =
      Boolean(consentPatientsLoading) || Boolean(registeredPersonsLoading);

    // "Todos" é sempre o primeiro: o dialog pré-seleciona o item [0].
    const all: MessagingPatientAudience = {
      // Sem repetir a mesma pessoa: evita colisão de `id` na seleção.
      id: "all",
      label: "Todos",
      patients: toMessaging(
        mergeOrganizationRecipients({
          appointmentPatients,
          consentPatients: consent,
          registeredClients,
        }),
      ),
      // No ecommerce os atendidos não entram, então o loading deles não conta.
      isLoading: isEcommerceOrg
        ? extrasLoading
        : Boolean(patientOrganizationLoading) || extrasLoading,
    };

    const appointments: MessagingPatientAudience = {
      id: "appointments",
      label: "Pacientes atendidos",
      patients: toMessaging(appointmentPatients),
      isLoading: patientOrganizationLoading,
    };

    const others: MessagingPatientAudience[] = [
      {
        id: "consent",
        label: consentAudienceLabel(isEcommerceOrg),
        // Só na composição deste público. "Todos" segue com o consentimento inteiro.
        // No ecommerce `appointmentPatients` já vem vazio, então nada é removido.
        patients: toMessaging(
          consentPatientsNotAttended(consent, appointmentPatients),
        ),
        isLoading: Boolean(consentPatientsLoading),
      },
      {
        id: "registered",
        label: "Clientes cadastrados",
        patients: toMessaging(registeredClients),
        isLoading: Boolean(registeredPersonsLoading),
      },
    ];

    return isEcommerceOrg ? [all, ...others] : [all, appointments, ...others];
  }, [
    enabled,
    vetApp,
    isEcommerceOrg,
    patients,
    patientOrganizationLoading,
    consentPatients,
    consentPatientsLoading,
    registeredPersons,
    registeredPersonsLoading,
  ]);
}
